// Execution progress (BLD-17). Each action of a validated initiative carries a completion percentage; weights
// (which sum to 100 on a validated initiative) turn them into the initiative's progress. Higher levels average
// their children; initiatives still in planning count as 0 so a KR never looks further ahead than its plan.
import { z } from "zod";
import { uuid } from "./setup.ts";

export const PROGRESS_STEP = 5;
export const PROGRESS_OPTIONS = Array.from({ length: 100 / PROGRESS_STEP + 1 }, (_, i) => i * PROGRESS_STEP);

/** Σ weight × percent / 100, rounded; null weights count as 0. */
export function initiativeProgress(actions: { weight: number | null; percent: number | null | undefined }[]): number {
  const total = actions.reduce((s, a) => s + (a.weight ?? 0) * (a.percent ?? 0), 0);
  return Math.round(total / 100);
}

/** Average of children, rounded; null when there is nothing to average. */
export function averageProgress(values: number[]): number | null {
  if (!values.length) return null;
  return Math.round(values.reduce((s, v) => s + v, 0) / values.length);
}

/** Progress of an initiative row as shown on aggregates: validated → its value, otherwise 0. */
export const effectiveProgress = (i: { status: string; progress: number | null }) => (i.status === "validated" ? (i.progress ?? 0) : 0);

export const progressUpdateSchema = z.object({
  updates: z
    .array(
      z.object({
        action_id: uuid,
        percent: z.number().int().min(0).max(100).refine((v) => v % PROGRESS_STEP === 0, `Use múltiplos de ${PROGRESS_STEP}.`),
        /** 0 when the action has no progress record yet. */
        expected_version: z.number().int().min(0),
      }),
    )
    .min(1, "Nenhuma alteração para salvar.")
    .max(50),
  note: z.string().trim().max(500, "Use no máximo 500 caracteres.").optional().transform((v) => (v ? v : null)),
});
export type ProgressUpdate = z.input<typeof progressUpdateSchema>;
